<template>
  <div class="workflow-container">
    <!-- 导航栏 -->
    <HeaderComponent />

    <!-- 页面标题 -->
    <section class="page-header">
      <div class="container">
        <h1 class="page-title">工作流中心</h1>
        <p class="page-subtitle">{{ labels[0]?.cnt || 0 }}+工作流，一键下载，导入即用，长期维护更新</p>
      </div>
    </section>

    <!-- 主内容区域 -->
    <section class="main-content">
      <div class="container">
        <!-- 筛选和搜索区域 -->
        <div class="filter-search">
          <div class="search-box">
            <font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
            <input type="text" placeholder="搜索工作流..." v-model="filter.search" @input="handleSearch">
            <button class="search-btn" @click="handleSearch">搜索</button>
          </div>
          <div class="filter-buttons">
            <button 
              v-for="label in labels" 
              :key="label.id"
              class="filter-btn"
              :class="{ active: filter.label_id === label.id }"
              @click="handleLabelChange(label.id)"
            >
              {{ label.name }}
              <span class="count">({{ label.cnt }})</span>
            </button>
          </div>
        </div>

        <!-- 工作流列表 -->
        <div class="workflow-grid">
          <div 
            v-for="workflow in paginatedWorkflows" 
            :key="workflow.id"
            class="workflow-card"
          >
            <div class="workflow-card-image">
              <div class="workflow-label">
                {{ workflow.label_name }}
              </div>
              <TagTypeComponent v-if="workflow.level" :tagType="workflow.level" />
              <img :src="$getProxyUrl(workflow.example_image)" :alt="workflow.title" class="workflow-img">
            </div>
            <div class="workflow-card-body">
              <h3 class="workflow-title">{{ workflow.title }}</h3>
              <p class="workflow-description">{{ workflow.description }}</p>
            </div>
            <div class="workflow-card-footer">
              <div class="workflow-meta">
                <span class="downloads"><font-awesome-icon :icon="['fas', 'download']" /> {{ workflow.download_count }}</span>
              </div>
              <div class="workflow-actions">
                <button class="btn btn-primary btn-small" @click="handleDownload(workflow)">下载</button>
                <button class="btn btn-secondary btn-small" @click="showDetail(workflow)">详情</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <PaginationComponent :currentPage="currentPage" :totalPages="totalPages" @update:currentPage="currentPage = $event"/>
      </div>
    </section>

    <!-- 页脚 -->
    <FooterComponent />

    <!-- 工作流详情弹出框 -->
    <WorkflowDetail 
      v-if="selectedWorkflow" 
      :workflow="selectedWorkflow" 
      :labels="labels"
      @close="hideDetail"
    />
  </div>
</template>

<script>
import HeaderComponent from '../components/Header.vue'
import FooterComponent from '../components/Footer.vue'
import PaginationComponent from '../components/PaginationComponent.vue'
import TagTypeComponent from '../components/TagTypeComponent.vue'
import WorkflowDetail from './WorkflowDetail.vue'
import { api } from '../api/index.js'

export default {
  name: 'WorkflowView',
  components: {
    HeaderComponent,
    FooterComponent,
    PaginationComponent,
    TagTypeComponent,
    WorkflowDetail
  },
  data() {
    return {
      filter: {
        search: '',
        label_id: null
      },
      
      totalPages: 0,
      currentPage: 1,
      pageSize: 12,
      selectedWorkflow: null,
      labels: [],
      workflows: []
    }
  },
  mounted() {
    this.loadLabels()
    this.loadWorkflows()
  },
  computed: {
    paginatedWorkflows() {
      return this.workflows
    }
  },
  watch: {
    currentPage() {
      this.loadWorkflows()
    }
  },
  methods: {
    async loadLabels() {
      try {
        const labels = await api.workflow.getLabels()
        // 计算总数
        const totalCount = labels.reduce((sum, label) => sum + label.cnt, 0)
        // 添加"全部"选项
        this.labels = [
          { id: null, name: '全部', cnt: totalCount },
          ...labels
        ]
      } catch (error) {
        console.error('加载标签失败:', error)
      }
    },
    async loadWorkflows() {
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (this.filter.search) {
          params.search = this.filter.search
        }

        if (this.filter.label_id !== null) {
          params.label_id = this.filter.label_id
        }

        const data = await api.workflow.getList(params)
        this.workflows = data.items
        this.totalPages = data.total_page
      } catch (error) {
        console.error('加载工作流失败:', error)
      }
    },
    handleLabelChange(label_id) {
      this.filter.label_id = label_id
      this.currentPage = 1
      this.loadWorkflows()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadWorkflows()
    },
    navigateToDetail(id) {
      this.$router.push(`/workflow/${id}`)
    },
    showDetail(workflow) {
      this.selectedWorkflow = workflow
    },
    hideDetail() {
      this.selectedWorkflow = null
    },
    async handleDownload(workflow) {
      try {
        const urls = await api.workflow.getDownload(workflow.id)
        if (urls && urls.length > 0) {
          urls.forEach(url => {
            window.open(url, '_blank')
          })
        } else {
          alert('暂无下载链接')
        }
      } catch (error) {
        console.error('下载失败:', error)
        alert('下载失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.workflow-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}



/* 页面标题 */
.page-header {
  background-color: #f8f9fa;
  padding: 120px 0 40px;
  border-bottom: 1px solid #eee;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
}

.page-subtitle {
  font-size: 18px;
  color: #666;
}

/* 主内容区域 */
.main-content {
  padding: 40px 0;
  min-height: calc(100vh - 400px);
}

/* 筛选和搜索区域 */
.filter-search {
  margin-bottom: 40px;
}

.search-box {
  position: relative;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.search-box i,
.search-box .search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  z-index: 1;
}

.search-box input {
  flex: 1;
  padding: 12px 15px 12px 45px;
  border: 1px solid #ddd;
  border-radius: 5px 0 0 5px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.search-btn {
  padding: 12px 24px;
  background-color: #667eea;
  color: white;
  border: none;
  border-radius: 0 5px 5px 0;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.search-btn:hover {
  background-color: #5568d3;
}

.search-box input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
}

.filter-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

.filter-btn.active {
  background-color: #667eea;
  border-color: #667eea;
  color: white;
}

.filter-btn .count {
  margin-left: 5px;
  font-size: 12px;
  opacity: 0.8;
}

/* 工作流列表 */
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
}

.workflow-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
}

.workflow-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.15);
}

.workflow-card-image {
  position: relative;
  height: 200px;
  overflow: hidden;
  background-color: #f8f9fa;
}

.workflow-card-image .tag-type {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
}

.workflow-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.workflow-card:hover .workflow-img {
  transform: scale(1.05);
}

.workflow-label {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: rgba(102, 126, 234, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  z-index: 2;
}

.workflow-card-body {
  padding: 20px;
}

.workflow-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}

.workflow-description {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.workflow-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px 20px;
}

.workflow-meta {
  display: flex;
  gap: 15px;
  font-size: 14px;
  color: #999;
}

.workflow-meta i {
  margin-right: 5px;
}

.workflow-actions {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  text-decoration: none;
  display: inline-block;
}

.btn-small {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background-color: #667eea;
  color: white;
}

.btn-primary:hover {
  background-color: #5568d3;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}





/* 响应式设计 */
@media (max-width: 768px) {
  .navbar-menu {
    display: none;
  }
  
  .navbar-toggle {
    display: block;
  }
  
  .workflow-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 28px;
  }
  
  .page-subtitle {
    font-size: 16px;
  }
  
  .workflow-grid {
    grid-template-columns: 1fr;
  }
}
</style>